import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Building2, Briefcase, Mail, MapPin, Phone, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { scanApi } from "../api/scan";
import { PageHeader } from "../components/PageHeader";
import { PageShell } from "../components/PageShell";
import { GlassCard } from "../components/GlassCard";
import { ScanNotesSection } from "../components/ScanNotesSection";
import { SkeletonList } from "../components/SkeletonList";

type VisitorCard = {
  fullName: string;
  phone?: string;
  email?: string;
  companyName?: string;
  position?: string;
  city?: string;
  activityType?: string;
  interestedProducts?: string;
  scanId?: string;
  notes?: string;
  tags?: string[];
};

export function VisitorContactPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedEventId } = useAppStore();
  const [card, setCard] = useState<VisitorCard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id || !selectedEventId) {
      setCard(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { scans } = await scanApi.list(selectedEventId);
        const hit = scans.find(
          (s) => s.scanTargetType === "visitor" && s.scanTargetId === id && s.visitor,
        );
        if (!cancelled) {
          setCard(
            hit?.visitor
              ? {
                  fullName: hit.visitor.fullName,
                  phone: hit.visitor.phone,
                  email: hit.visitor.email,
                  companyName: hit.visitor.companyName,
                  position: hit.visitor.position,
                  city: hit.visitor.city,
                  activityType: hit.visitor.activityType,
                  interestedProducts: hit.visitor.interestedProducts,
                  scanId: hit.id,
                  notes: hit.notes,
                  tags: hit.tags,
                }
              : null,
          );
        }
      } catch {
        if (!cancelled) setCard(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, selectedEventId]);

  return (
    <>
      <PageHeader title={t("visitor.contact_title")} back backTo="/history" />
      <PageShell>
        {loading ? (
          <SkeletonList variant="detail" />
        ) : !card ? (
          <div className="pt-16 text-center space-y-3">
            <p className="text-gray-500 text-[14px]">{t("visitor.contact_not_found")}</p>
            <button
              type="button"
              className="text-indigo-600 text-[14px] font-medium"
              onClick={() => navigate("/history")}
            >
              {t("visitor.to_history")}
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            <GlassCard variant="elevated" padding="lg">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <User size={26} className="text-indigo-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-[18px] text-gray-900 font-medium leading-snug">{card.fullName}</p>
                  <span className="inline-flex mt-1 text-[11px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    {t("visitor.visitor")}
                  </span>
                </div>
              </div>
              <div className="space-y-2.5">
                {card.companyName ? (
                  <p className="text-[13px] text-gray-600 flex items-center gap-2">
                    <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                    {card.companyName}
                  </p>
                ) : null}
                {card.city ? (
                  <p className="text-[13px] text-gray-600 flex items-center gap-2">
                    <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                    {card.city}
                  </p>
                ) : null}
                {card.position ? (
                  <p className="text-[13px] text-gray-600 flex items-center gap-2">
                    <Briefcase size={14} className="text-gray-400 flex-shrink-0" />
                    {card.position}
                  </p>
                ) : null}
                {card.activityType ? (
                  <p className="text-[13px] text-gray-600">
                    <span className="text-gray-400">{t("visitor.activity_type")}: </span>
                    {card.activityType}
                  </p>
                ) : null}
                {card.interestedProducts ? (
                  <p className="text-[13px] text-gray-600">
                    <span className="text-gray-400">{t("visitor.interested_products")}: </span>
                    {card.interestedProducts}
                  </p>
                ) : null}
                {card.phone ? (
                  <a
                    href={`tel:${card.phone}`}
                    className="text-[13px] text-indigo-600 flex items-center gap-2"
                  >
                    <Phone size={14} className="flex-shrink-0" />
                    {card.phone}
                  </a>
                ) : null}
                {card.email ? (
                  <a
                    href={`mailto:${card.email}`}
                    className="text-[13px] text-indigo-600 flex items-center gap-2"
                  >
                    <Mail size={14} className="flex-shrink-0" />
                    {card.email}
                  </a>
                ) : null}
              </div>
            </GlassCard>

            {selectedEventId ? (
              <ScanNotesSection
                eventId={selectedEventId}
                scanId={card.scanId}
                rawNotes={card.notes}
                tags={card.tags}
                resolveScanId={async () => {
                  if (card.scanId) return card.scanId;
                  if (!id) return undefined;
                  const { scans } = await scanApi.list(selectedEventId);
                  return scans.find(
                    (s) => s.scanTargetType === "visitor" && s.scanTargetId === id,
                  )?.id;
                }}
                onSaved={({ scanId, notes, tags }) => {
                  setCard((prev) =>
                    prev ? { ...prev, scanId, notes, tags } : prev,
                  );
                }}
              />
            ) : null}
          </div>
        )}
      </PageShell>
    </>
  );
}
